import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import AnimatedSection from '../components/AnimatedSection';
import StaggeredList from '../components/StaggeredList';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { 
  Building, 
  Users, 
  Award, 
  Globe, 
  Handshake, 
  TrendingUp, 
  CheckCircle,
  Star,
  Mail,
  Phone,
  ExternalLink,
  MapPin,
  Target,
  Shield,
  Zap
} from 'lucide-react';

const PartnersPage = () => {
  const partnerTypes = [
    {
      title: "Healthcare Systems",
      description: "Hospital networks and integrated health systems bringing Cordflex® to their units",
      icon: <Building className="h-8 w-8" />,
      color: "#214140",
      benefits: [
        "Volume pricing and custom contracts",
        "Dedicated implementation support",
        "Usage and feedback tracking",
        "Priority customer service"
      ]
    },
    {
      title: "Medical Device Distributors",
      description: "Authorized distributors bringing Cordflex to healthcare providers in their regions",
      icon: <Globe className="h-8 w-8" />,
      color: "#8BBAB8",
      benefits: [
        "Exclusive territory rights",
        "Marketing support and materials",
        "Training programs",
        "Competitive margins"
      ]
    },
    {
      title: "Clinical Research Organizations",
      description: "Research partners studying Cordflex in clinical settings and developing new applications",
      icon: <Award className="h-8 w-8" />,
      color: "#1F8051",
      benefits: [
        "Access to study data",
        "Research collaboration opportunities",
        "Publication support",
        "Early access to new products"
      ]
    },
  ];

  const partnershipBenefits = [
    {
      title: "Nurse-Driven Innovation",
      description: "Partner on a line management solution designed with nurses",
      icon: <Target className="h-6 w-6" />,
      color: "#214140"
    },
    {
      title: "Evaluated and Adopted",
      description: "Trialed and adopted by hospital ICU teams",
      icon: <CheckCircle className="h-6 w-6" />,
      color: "#1F8051"
    },
    {
      title: "Comprehensive Support",
      description: "Full training, marketing, and technical support",
      icon: <Shield className="h-6 w-6" />,
      color: "#8BBAB8"
    },
    {
      title: "Growth Opportunity",
      description: "Expanding market with significant growth potential",
      icon: <TrendingUp className="h-6 w-6" />,
      color: "#214140"
    }
  ];

  const partnershipProcess = [
    {
      step: 1,
      title: "Initial Discussion",
      description: "Contact our partnership team to discuss opportunities and alignment"
    },
    {
      step: 2,
      title: "Evaluation",
      description: "We review your organization's capabilities and market fit"
    },
    {
      step: 3,
      title: "Agreement",
      description: "Negotiate terms and finalize partnership agreement"
    },
    {
      step: 4,
      title: "Onboarding",
      description: "Training, setup, and launch support to ensure success"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main className="pt-24">
        {/* Hero Section */}
        <section className="py-20 bg-gradient-to-b from-white to-gray-50">
          <div className="container mx-auto px-4">
            <AnimatedSection animation="fade-up">
              <div className="text-center max-w-4xl mx-auto">
                <h1 className="text-4xl lg:text-5xl font-bold mb-6" style={{ color: '#214140' }}>
                  Partner with Us
                </h1>
                <p className="text-xl text-gray-600 leading-relaxed mb-8">
                  Join our network of healthcare leaders, distributors, and technology partners 
                  who are transforming critical care with innovative line management solutions.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button
                    className="px-8 py-4 text-lg font-semibold hover:opacity-90 transition-opacity duration-200"
                    style={{ backgroundColor: '#214140', color: 'white' }}
                  >
                    <Handshake className="h-5 w-5 mr-2" />
                    Become a Partner
                  </Button>
                  <Button
                    variant="outline"
                    className="px-8 py-4 text-lg font-semibold"
                    onClick={() => window.location.href = '/contact'}
                  >
                    Partner Inquiry
                  </Button>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </section>

        {/* Partnership Types */}
        <section id="partnership-opportunities" className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <AnimatedSection animation="fade-up">
              <div className="text-center mb-16">
                <h2 className="text-3xl lg:text-4xl font-bold mb-4" style={{ color: '#214140' }}>
                  Partnership Opportunities
                </h2>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                  We work with diverse organizations to expand access to Cordflex and improve 
                  patient outcomes across the healthcare continuum.
                </p>
              </div>
            </AnimatedSection>

            <StaggeredList delay={150} className="grid md:grid-cols-2 gap-8">
              {partnerTypes.map((type, index) => (
                <Card key={index} className="bg-white shadow-lg hover:shadow-xl transition-shadow duration-300">
                  <CardHeader className="pb-4">
                    <div className="flex items-center justify-between mb-4">
                      <div
                        className="w-16 h-16 rounded-full flex items-center justify-center"
                        style={{ backgroundColor: type.color, color: 'white' }}
                      >
                        {type.icon}
                      </div>
                    </div>
                    <CardTitle className="text-xl mb-2" style={{ color: '#214140' }}>
                      {type.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600 mb-6">{type.description}</p>
                    <div>
                      <h4 className="font-semibold mb-3" style={{ color: '#214140' }}>
                        Partnership Benefits:
                      </h4>
                      <ul className="space-y-2">
                        {type.benefits.map((benefit, benefitIndex) => (
                          <li key={benefitIndex} className="flex items-start space-x-2">
                            <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                            <span className="text-sm text-gray-600">{benefit}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </StaggeredList>
          </div>
        </section>

        {/* Featured Partners - HIDDEN FOR LAUNCH */}

        {/* Partnership Process */}
        <section className="py-20" style={{ backgroundColor: '#273139' }}>
          <div className="container mx-auto px-4">
            <AnimatedSection animation="fade-up">
              <div className="text-center mb-16">
                <h2 className="text-3xl lg:text-4xl font-bold mb-4 text-white">
                  Partnership Process
                </h2>
                <p className="text-xl text-gray-300">
                  Simple steps to start our partnership journey
                </p>
              </div>
            </AnimatedSection>

            <div className="max-w-4xl mx-auto">
              <StaggeredList delay={200} className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                {partnershipProcess.map((step, index) => (
                  <Card key={index} className="bg-white shadow-lg text-center">
                    <CardContent className="p-6">
                      <div className="w-16 h-16 rounded-full flex items-center justify-center mb-4 mx-auto" style={{ backgroundColor: '#8BBAB8', color: 'white' }}>
                        <span className="text-2xl font-bold">{step.step}</span>
                      </div>
                      <h3 className="text-lg font-semibold mb-2" style={{ color: '#214140' }}>
                        {step.title}
                      </h3>
                      <p className="text-gray-600 text-sm">
                        {step.description}
                      </p>
                    </CardContent>
                  </Card>
                ))}
              </StaggeredList>
            </div>
          </div>
        </section>

        {/* Partnership Benefits */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <AnimatedSection animation="fade-up">
              <div className="text-center mb-16">
                <h2 className="text-3xl lg:text-4xl font-bold mb-4" style={{ color: '#214140' }}>
                  Why Partner with Nursetech Medical™?
                </h2>
                <p className="text-xl text-gray-600">
                  Strong partnerships built on mutual success and shared values
                </p>
              </div>
            </AnimatedSection>

            <StaggeredList delay={150} className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {partnershipBenefits.map((benefit, index) => (
                <Card key={index} className="bg-white shadow-lg hover:shadow-xl transition-shadow duration-300 text-center">
                  <CardContent className="p-6">
                    <div
                      className="w-16 h-16 rounded-full flex items-center justify-center mb-4 mx-auto"
                      style={{ backgroundColor: benefit.color, color: 'white' }}
                    >
                      {benefit.icon}
                    </div>
                    <h3 className="text-lg font-semibold mb-2" style={{ color: '#214140' }}>
                      {benefit.title}
                    </h3>
                    <p className="text-gray-600 text-sm">
                      {benefit.description}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </StaggeredList>
          </div>
        </section>

        {/* Partner Resources - HIDDEN FOR LAUNCH */}

        {/* CTA Section */}
        <section className="py-20" style={{ backgroundColor: '#DFEAF0' }}>
          <div className="container mx-auto px-4">
            <AnimatedSection animation="fade-up">
              <div className="text-center max-w-3xl mx-auto">
                <h2 className="text-3xl lg:text-4xl font-bold mb-6" style={{ color: '#214140' }}>
                  Ready to Partner with Us?
                </h2>
                <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                  Join our growing network of partners who are transforming healthcare outcomes 
                  with innovative line management solutions.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button
                    className="px-8 py-4 text-lg font-semibold hover:opacity-90 transition-opacity duration-200"
                    style={{ backgroundColor: '#214140', color: 'white' }}
                    onClick={() => window.location.href = '/contact'}
                  >
                    <Mail className="h-5 w-5 mr-2" />
                    Start Partnership Discussion
                  </Button>
                  {/* <Button
                    variant="outline"
                    className="px-8 py-4 text-lg font-semibold"
                  >
                    <Phone className="h-5 w-5 mr-2" />
                    Call: (410) 935-9089
                  </Button> */}
                </div>
              </div>
            </AnimatedSection>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default PartnersPage;